import type { ShipmentTemplate } from '~api/userprofile';

import { Fragment, ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { DropdownMenu, InteractiveTable, Text } from '@ingrid/luterkort-react';

import { RoleGuard } from '~src/components/RoleGuard/RoleGuard';
import { useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';
import { AppRoute } from '~src/main/routes';

import { useShipmentTemplateRemoveMutation } from '../mutations';

interface Props {
  template: ShipmentTemplate;
  displayEditAction?: boolean;
  trigger: ReactNode;
}

export function Menu({ displayEditAction, trigger, template }: Props) {
  const { setHighlightedKey } = InteractiveTable.useContext();
  const { t } = useTranslation();
  const { toast } = useToast();

  const removeMutation = useShipmentTemplateRemoveMutation();

  const handleRemoveTemplate = async () => {
    const promise = removeMutation.mutateAsync(template.id!);

    await toast.promise(promise, {
      loading: { title: t('shipmentTemplates.details.removeLoading') },
      success: { title: t('shipmentTemplates.details.removeSuccess') },
      error: { title: t('shipmentTemplates.details.removeError') },
    });

    setHighlightedKey(null);
  };

  return (
    <Fragment>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>{trigger}</DropdownMenu.Trigger>

        <DropdownMenu.Content>
          {displayEditAction && (
            <RoleGuard roles={['transport:edit']} withTooltip>
              <DropdownMenu.Item asChild>
                <RouterLink to={`${AppRoute.EDIT}/${template.id!}`}>{t('common.edit')}</RouterLink>
              </DropdownMenu.Item>
            </RoleGuard>
          )}

          <RoleGuard roles={['transport:edit']} withTooltip>
            <DropdownMenu.Item asChild>
              <RouterLink to={`${AppRoute.DUPLICATE}/${template.id!}`}>{t('common.duplicate')}</RouterLink>
            </DropdownMenu.Item>
          </RoleGuard>

          <RoleGuard roles={['transport:edit']} withTooltip>
            <DropdownMenu.Item onClick={handleRemoveTemplate}>
              <Text variant="critical">{t('common.delete')}</Text>
            </DropdownMenu.Item>
          </RoleGuard>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </Fragment>
  );
}
